import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

const QRCodeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [qrCode, setQRCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const fetchQRCode = useCallback(async () => {
    try {
      const response = await axios.get(`/api/qr/${id}`);
      setQRCode(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load QR code');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchQRCode();
  }, [fetchQRCode]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const downloadQR = () => {
    const svg = document.getElementById('qr-code-svg');
    if (svg) {
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        const pngFile = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.download = `qr-code-${qrCode.short_code}.png`;
        downloadLink.href = pngFile;
        downloadLink.click();
      };
      img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
    }
  };

  if (loading) {
    return <div className="loading">Loading QR code details...</div>;
  }

  if (error) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center' }}>
          <div className="error" style={{ marginBottom: '20px' }}>{error}</div>
          <button onClick={() => navigate('/qr-codes')} className="btn btn-secondary">
            Back to QR Codes
          </button>
        </div>
      </div>
    );
  }

  if (!qrCode) {
    return <div className="container">QR code not found</div>;
  }

  return (
    <div className="container">
      <div className="dashboard-header">
        <h1 style={{ 
          background: 'var(--gradient-primary)', 
          WebkitBackgroundClip: 'text', 
          WebkitTextFillColor: 'transparent' 
        }}>
          {qrCode.title || 'QR Code Details'}
        </h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Link to={`/qr-codes/edit/${id}`} className="btn btn-primary">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Edit
          </Link>
          <Link to="/qr-codes" className="btn btn-secondary">
            ← Back
          </Link>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 400px) 1fr', gap: '24px' }}>
        {/* QR Code Image */}
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ 
            background: 'white', 
            padding: '32px', 
            borderRadius: 'var(--radius-lg)',
            marginBottom: '24px',
            display: 'inline-block'
          }}>
            <QRCodeSVG 
              id="qr-code-svg"
              value={qrCode.shortUrl} 
              size={240} 
              level="H"
            />
          </div>
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={downloadQR} className="btn btn-primary">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Download PNG
            </button>
            <button 
              onClick={() => copyToClipboard(qrCode.shortUrl)} 
              className="btn btn-secondary"
            >
              {copied ? (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                  Copy URL
                </>
              )}
            </button>
          </div>
        </div>

        {/* Details */}
        <div>
          <div className="card">
            <h2>Information</h2>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                color: 'var(--text-muted)', 
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: '8px'
              }}>Short URL</label>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                background: 'var(--bg-elevated)',
                padding: '12px 16px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-color)'
              }}>
                <code style={{ flex: 1, fontSize: '14px', wordBreak: 'break-all' }}>
                  {qrCode.shortUrl}
                </code>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                color: 'var(--text-muted)', 
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: '8px'
              }}>Original URL</label>
              <a 
                href={qrCode.original_url} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ wordBreak: 'break-all' }}
              >
                {qrCode.original_url}
              </a>
            </div>

            {qrCode.description && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  color: 'var(--text-muted)', 
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginBottom: '8px'
                }}>Description</label>
                <p style={{ color: 'var(--text-secondary)' }}>{qrCode.description}</p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  color: 'var(--text-muted)', 
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginBottom: '4px'
                }}>Created</label>
                <span style={{ color: 'var(--text-secondary)' }}>
                  {new Date(qrCode.created_at).toLocaleDateString()}
                </span>
              </div>
              <div>
                <label style={{ 
                  display: 'block', 
                  color: 'var(--text-muted)', 
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginBottom: '4px'
                }}>Short Code</label>
                <code>{qrCode.short_code}</code>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="card">
            <h2>Statistics</h2>
            <div className="stats-grid" style={{ marginBottom: 0 }}>
              <div className="stat-card">
                <h3>Total Clicks</h3>
                <div className="value">{qrCode.totalClicks || 0}</div>
              </div>
              <div className="stat-card">
                <h3>Today</h3>
                <div className="value">{qrCode.todayClicks || 0}</div>
              </div>
            </div>
            <Link 
              to={`/analytics/${id}`} 
              className="btn btn-secondary"
              style={{ marginTop: '20px' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="20" x2="18" y2="10"/>
                <line x1="12" y1="20" x2="12" y2="4"/>
                <line x1="6" y1="20" x2="6" y2="14"/>
              </svg>
              View Detailed Analytics
            </Link>
          </div>

          {/* Smart Redirect Settings */}
          {(qrCode.ios_deep_link || qrCode.ios_app_store_url || qrCode.android_deep_link || qrCode.android_play_store_url || qrCode.web_url) && (
            <div className="card">
              <h2>Smart Redirect Settings</h2>
              
              {(qrCode.ios_deep_link || qrCode.ios_app_store_url) && (
                <div style={{ 
                  padding: '16px', 
                  background: 'var(--bg-elevated)', 
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '12px',
                  border: '1px solid var(--border-color)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-primary)" strokeWidth="2">
                      <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                      <line x1="12" y1="18" x2="12.01" y2="18"/>
                    </svg>
                    <strong>iOS</strong>
                  </div>
                  {qrCode.ios_deep_link && (
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Deep Link:</span>
                      <code style={{ display: 'block', marginTop: '4px', fontSize: '13px' }}>{qrCode.ios_deep_link}</code>
                    </div>
                  )}
                  {qrCode.ios_app_store_url && (
                    <div>
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>App Store:</span>
                      <a href={qrCode.ios_app_store_url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: '4px', fontSize: '13px' }}>
                        {qrCode.ios_app_store_url}
                      </a>
                    </div>
                  )}
                </div>
              )}

              {(qrCode.android_deep_link || qrCode.android_play_store_url) && (
                <div style={{ 
                  padding: '16px', 
                  background: 'var(--bg-elevated)', 
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '12px',
                  border: '1px solid var(--border-color)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-tertiary)" strokeWidth="2">
                      <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                      <line x1="12" y1="18" x2="12.01" y2="18"/>
                    </svg>
                    <strong>Android</strong>
                  </div>
                  {qrCode.android_deep_link && (
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Deep Link:</span>
                      <code style={{ display: 'block', marginTop: '4px', fontSize: '13px' }}>{qrCode.android_deep_link}</code>
                    </div>
                  )}
                  {qrCode.android_play_store_url && (
                    <div>
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Play Store:</span>
                      <a href={qrCode.android_play_store_url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: '4px', fontSize: '13px' }}>
                        {qrCode.android_play_store_url}
                      </a>
                    </div>
                  )}
                </div>
              )}

              {qrCode.web_url && (
                <div style={{ 
                  padding: '16px', 
                  background: 'var(--bg-elevated)', 
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-color)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-secondary)" strokeWidth="2">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                      <line x1="8" y1="21" x2="16" y2="21"/>
                      <line x1="12" y1="17" x2="12" y2="21"/>
                    </svg>
                    <strong>Desktop</strong>
                  </div>
                  <a href={qrCode.web_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px' }}>
                    {qrCode.web_url}
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRCodeDetail;
